import React, { useState, useEffect } from 'react';

const MySponsorshipsPage = ({ currentUserId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('initiated');

  useEffect(() => {
    fetchMySponsorships();
  }, []);

  const fetchMySponsorships = async () => {
    try {
      const response = await fetch('/api/v1/sponsorships/my_sponsorships', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const responseData = await response.json();

      if (response.ok) {
        setData(responseData.data);
      } else {
        setError(responseData.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'bg-warning text-dark', text: 'En attente' },
      approved: { class: 'bg-success', text: 'Approuvé' },
      completed: { class: 'bg-primary', text: 'Complété' },
      rejected: { class: 'bg-danger', text: 'Rejeté' }
    };

    const badge = badges[status] || { class: 'bg-secondary', text: status };
    
    return (
      <span className={`badge ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      sympathisant: 'Adhérent Sympathisant',
      participant: 'Adhérent Participant',
      actif: 'Adhérent Actif'
    };
    return labels[level] || level;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const SponsorshipCard = ({ sponsorship, type }) => (
    <div className="card mb-3">
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-8">
            <h6 className="card-title mb-1" style={{ color: '#00256C' }}>
              {sponsorship.filleul_email}
            </h6>
            <p className="card-text mb-2">
              <small className="text-muted">
                {getLevelLabel(sponsorship.requested_level)}
              </small>
            </p>
            {type === 'supported' && (
              <p className="card-text mb-1">
                <small>
                  <strong>Initiateur:</strong> {sponsorship.initiator_name}
                </small>
              </p>
            )}
            <p className="card-text">
              <small className="text-muted">
                Créé le {formatDate(sponsorship.created_at)}
              </small>
            </p>
          </div>
          <div className="col-md-4 text-md-end">
            <div className="mb-2">
              {getStatusBadge(sponsorship.status)}
            </div>
            {sponsorship.status === 'pending' && (
              <div className="mb-2">
                <small className="text-muted">
                  {sponsorship.supporters_count} / {sponsorship.required_supporters_count} soutiens
                </small>
                <div className="progress" style={{ height: '5px' }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ 
                      width: `${(sponsorship.supporters_count / sponsorship.required_supporters_count) * 100}%`,
                      backgroundColor: '#1E4F8A'
                    }}
                  ></div>
                </div>
              </div>
            )}
            <a 
              href={`/sponsorships/${sponsorship.id}`}
              className="btn btn-sm"
              style={{ 
                backgroundColor: '#1E4F8A',
                borderColor: '#1E4F8A',
                color: 'white'
              }}
            >
              Voir détails
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#00256C' }}>Mes Parrainages</h2>
        <a 
          href="/sponsorships/new"
          className="btn"
          style={{ 
            backgroundColor: '#1E4F8A',
            borderColor: '#1E4F8A',
            color: 'white'
          }}
        >
          <i className="fas fa-plus me-2"></i>
          Nouvelle Demande
        </a>
      </div>

      {/* Onglets */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'initiated' ? 'active' : ''}`}
            onClick={() => setActiveTab('initiated')}
            style={{
              color: activeTab === 'initiated' ? '#1E4F8A' : '#6c757d',
              borderBottomColor: activeTab === 'initiated' ? '#1E4F8A' : 'transparent'
            }}
          >
            Demandes Initiées 
            {data?.initiated_sponsorships?.length > 0 && (
              <span className="badge bg-secondary ms-1">
                {data.initiated_sponsorships.length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'supported' ? 'active' : ''}`}
            onClick={() => setActiveTab('supported')}
            style={{
              color: activeTab === 'supported' ? '#1E4F8A' : '#6c757d',
              borderBottomColor: activeTab === 'supported' ? '#1E4F8A' : 'transparent'
            }}
          >
            Demandes Soutenues
            {data?.supported_sponsorships?.length > 0 && (
              <span className="badge bg-secondary ms-1">
                {data.supported_sponsorships.length}
              </span>
            )}
          </button>
        </li>
      </ul>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {activeTab === 'initiated' && (
          <div className="tab-pane fade show active">
            {data?.initiated_sponsorships?.length > 0 ? (
              <>
                <div className="mb-3">
                  <h5 style={{ color: '#00256C' }}>
                    Demandes que vous avez initiées ({data.initiated_sponsorships.length})
                  </h5>
                  <p className="text-muted">
                    Voici les demandes de parrainage que vous avez créées.
                  </p>
                </div>
                {data.initiated_sponsorships.map(sponsorship => (
                  <SponsorshipCard 
                    key={sponsorship.id} 
                    sponsorship={sponsorship} 
                    type="initiated"
                  />
                ))}
              </>
            ) : (
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="fas fa-user-plus fa-3x text-muted"></i>
                </div>
                <h5 className="text-muted">Aucune demande initiée</h5>
                <p className="text-muted">
                  Vous n'avez encore initié aucune demande de parrainage.
                </p>
                <a 
                  href="/sponsorships/new"
                  className="btn"
                  style={{ 
                    backgroundColor: '#1E4F8A',
                    borderColor: '#1E4F8A',
                    color: 'white'
                  }}
                >
                  Créer ma première demande
                </a>
              </div>
            )}
          </div>
        )}

        {activeTab === 'supported' && (
          <div className="tab-pane fade show active">
            {data?.supported_sponsorships?.length > 0 ? (
              <>
                <div className="mb-3">
                  <h5 style={{ color: '#00256C' }}>
                    Demandes que vous soutenez ({data.supported_sponsorships.length})
                  </h5>
                  <p className="text-muted">
                    Voici les demandes de parrainage que vous avez soutenues.
                  </p>
                </div>
                {data.supported_sponsorships.map(sponsorship => (
                  <SponsorshipCard 
                    key={sponsorship.id} 
                    sponsorship={sponsorship} 
                    type="supported"
                  />
                ))}
              </>
            ) : (
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="fas fa-handshake fa-3x text-muted"></i>
                </div>
                <h5 className="text-muted">Aucune demande soutenue</h5>
                <p className="text-muted">
                  Vous n'avez encore soutenu aucune demande de parrainage.
                </p>
                <a 
                  href="/sponsorships"
                  className="btn btn-outline-primary"
                >
                  Voir les demandes en attente
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Statistiques */}
      {(data?.initiated_sponsorships?.length > 0 || data?.supported_sponsorships?.length > 0) && (
        <div className="row mt-5">
          <div className="col-md-6">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h4 className="text-primary">
                  {data?.initiated_sponsorships?.length || 0}
                </h4>
                <p className="text-muted mb-0">Demandes initiées</p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h4 className="text-success">
                  {data?.supported_sponsorships?.length || 0}
                </h4>
                <p className="text-muted mb-0">Demandes soutenues</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySponsorshipsPage;